/**
 * Has to start with `+`, then between 7 and 15 digits; spaces not allowed
 * @see https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s03.html
 */
export const phoneNumberPattern = /^\+?(?:[0-9]){4,14}[0-9]$/;
export const geolocationPattern =
  /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?),\s*[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;

export interface ImageDimensionValidationResult {
  isValid: boolean;
  width?: number;
  height?: number;
  errorMessage?: string;
}

/**
 * Validates that an image file has the exact specified width and height.
 * Resolves with validation status, actual dimensions, and context-specific error message if invalid.
 */
export const validateExactImageDimensions = (
  file: File,
  targetWidth: number,
  targetHeight: number,
  _contextName?: string,
): Promise<ImageDimensionValidationResult> => {
  return new Promise((resolve) => {
    // Explicitly reject WebP format
    if (file.type === 'image/webp' || /\.webp$/i.test(file.name)) {
      resolve({
        isValid: false,
        errorMessage:
          'WebP format is not supported. Please upload a JPEG or PNG image.',
      });

      return;
    }

    const isAllowedImage =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpg' ||
      /\.(jpe?g|png)$/i.test(file.name);

    if (!isAllowedImage) {
      resolve({
        isValid: false,
        errorMessage: 'Please upload a valid image file (JPEG or PNG).',
      });

      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;
      URL.revokeObjectURL(objectUrl);

      if (width === targetWidth && height === targetHeight) {
        resolve({ isValid: true, width, height });
      } else {
        resolve({
          isValid: false,
          width,
          height,
          errorMessage: `Please upload an image with dimensions of exactly ${targetWidth} × ${targetHeight} pixels.`,
        });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        isValid: false,
        errorMessage:
          'Failed to decode image. Please upload a valid image file.',
      });
    };

    img.src = objectUrl;
  });
};
